import type {
  LayoutEngine,
  LayoutInput,
  LayoutResult,
  NodeBounds,
  ConnectorPath,
  BoundingRect,
} from './types';
import type { MindMapNode } from '@/model/types';
import { cubicBezierPath } from '@/utils/geometry';
import { getStyleForLevel } from '@/theme/defaultTheme';
import { defaultTheme } from '@/theme/defaultTheme';
import { computeNodeExtraHeight } from './nodeExtraHeight';

interface SubtreeSize {
  width: number;
  height: number;
}

/**
 * Horizontal tree layout: root at center, children radiate left and right.
 * Right children use positive ordering, left children use the second half.
 */
export const horizontalTreeLayout: LayoutEngine = {
  compute(input: LayoutInput): LayoutResult {
    const { nodes, rootNodeId, settings, measureText } = input;
    const root = nodes.get(rootNodeId);
    if (!root) {
      return { nodeBounds: new Map(), connectorPaths: [], totalBounds: { x: 0, y: 0, width: 0, height: 0 } };
    }

    const bounds = new Map<string, NodeBounds>();
    const connectors: ConnectorPath[] = [];

    // Measure all visible nodes
    const measured = new Map<string, { width: number; height: number }>();
    for (const [id, node] of nodes) {
      if (node.position) continue; // Skip disconnected nodes
      const depth = getDepth(node, nodes);
      const style = getStyleForLevel(defaultTheme, depth);
      const textSize = measureText(
        node.text || ' ',
        node.style?.fontSize ?? style.fontSize,
        node.style?.fontWeight ?? style.fontWeight,
      );
      let width = textSize.width + style.paddingX * 2;
      let height = textSize.height + style.paddingY * 2 + computeNodeExtraHeight(node);
      if (node.sizeOverride) {
        width = Math.max(width, node.sizeOverride.width);
        height = Math.max(height, node.sizeOverride.height);
      }
      measured.set(id, { width, height });
    }

    // Split root children into right and left halves
    const visibleChildren = root.collapsed
      ? []
      : root.childIds.filter((id) => nodes.has(id));
    const midpoint = Math.ceil(visibleChildren.length / 2);
    const rightChildren = visibleChildren.slice(0, midpoint);
    const leftChildren = visibleChildren.slice(midpoint);

    // Compute subtree sizes
    const subtreeSizes = new Map<string, SubtreeSize>();
    const computeSubtreeSize = (nodeId: string, direction: 'right' | 'left'): SubtreeSize => {
      const node = nodes.get(nodeId);
      const size = measured.get(nodeId) ?? { width: 100, height: 30 };
      if (!node || node.collapsed || node.childIds.length === 0) {
        const result = { width: size.width, height: size.height };
        subtreeSizes.set(nodeId, result);
        return result;
      }

      const visibleKids = node.childIds.filter((id) => nodes.has(id));
      let childrenHeight = 0;
      let maxChildWidth = 0;
      for (const childId of visibleKids) {
        const childSize = computeSubtreeSize(childId, direction);
        childrenHeight += childSize.height;
        maxChildWidth = Math.max(maxChildWidth, childSize.width);
      }
      childrenHeight += Math.max(0, visibleKids.length - 1) * settings.verticalGap;

      const result = {
        width: size.width + settings.horizontalGap + maxChildWidth,
        height: Math.max(size.height, childrenHeight),
      };
      subtreeSizes.set(nodeId, result);
      return result;
    };

    // Compute sizes for both sides
    for (const childId of rightChildren) computeSubtreeSize(childId, 'right');
    for (const childId of leftChildren) computeSubtreeSize(childId, 'left');

    // Position root at origin
    const rootSize = measured.get(rootNodeId) ?? { width: 120, height: 40 };
    bounds.set(rootNodeId, {
      id: rootNodeId,
      x: -rootSize.width / 2,
      y: -rootSize.height / 2,
      width: rootSize.width,
      height: rootSize.height,
      centerX: 0,
      centerY: 0,
      depth: 0,
    });

    // Position children recursively
    const positionChildren = (
      parentId: string,
      childIds: string[],
      direction: 'right' | 'left',
      parentBounds: NodeBounds,
    ) => {
      if (childIds.length === 0) return;

      let totalHeight = 0;
      for (const childId of childIds) {
        const subtreeSize = subtreeSizes.get(childId) ?? { width: 100, height: 30 };
        totalHeight += subtreeSize.height;
      }
      totalHeight += Math.max(0, childIds.length - 1) * settings.verticalGap;

      let currentY = parentBounds.centerY - totalHeight / 2;

      for (const childId of childIds) {
        const childNode = nodes.get(childId);
        if (!childNode) continue;

        const childSize = measured.get(childId) ?? { width: 100, height: 30 };
        const subtreeSize = subtreeSizes.get(childId) ?? { width: 100, height: 30 };
        const depth = getDepth(childNode, nodes);

        const childCenterY = currentY + subtreeSize.height / 2;
        const childX =
          direction === 'right'
            ? parentBounds.x + parentBounds.width + settings.horizontalGap
            : parentBounds.x - settings.horizontalGap - childSize.width;

        const childBounds: NodeBounds = {
          id: childId,
          x: childX,
          y: childCenterY - childSize.height / 2,
          width: childSize.width,
          height: childSize.height,
          centerX: childX + childSize.width / 2,
          centerY: childCenterY,
          depth,
        };
        bounds.set(childId, childBounds);

        // Connector from parent edge to child edge
        const fromX =
          direction === 'right'
            ? parentBounds.x + parentBounds.width
            : parentBounds.x;
        const toX =
          direction === 'right' ? childBounds.x : childBounds.x + childBounds.width;

        connectors.push({
          parentId,
          childId,
          path: cubicBezierPath(
            { x: fromX, y: parentBounds.centerY },
            { x: toX, y: childBounds.centerY },
          ),
        });

        // Recurse for grandchildren
        if (!childNode.collapsed) {
          const grandchildren = childNode.childIds.filter((id) => nodes.has(id));
          positionChildren(childId, grandchildren, direction, childBounds);
        }

        currentY += subtreeSize.height + settings.verticalGap;
      }
    };

    const rootBounds = bounds.get(rootNodeId)!;
    positionChildren(rootNodeId, rightChildren, 'right', rootBounds);
    positionChildren(rootNodeId, leftChildren, 'left', rootBounds);

    // Apply manual position overrides and handle disconnected nodes
    let hasOverrides = false;
    for (const [id, node] of nodes) {
      if (!node.position) continue;
      // Don't show manually positioned nodes if any ancestor is collapsed
      if (hasCollapsedAncestor(node, nodes)) {
        bounds.delete(id);
        continue;
      }
      hasOverrides = true;
      const size = measured.get(id) ?? { width: 100, height: 30 };
      const existing = bounds.get(id);
      bounds.set(id, {
        id,
        x: node.position.x,
        y: node.position.y,
        width: size.width,
        height: size.height,
        centerX: node.position.x + size.width / 2,
        centerY: node.position.y + size.height / 2,
        depth: existing?.depth ?? 0,
      });
    }

    // Rebuild connectors from final bounds if any positions were overridden
    if (hasOverrides) {
      connectors.length = 0;
      for (const [id, node] of nodes) {
        if (!node.parentId) continue;
        const childB = bounds.get(id);
        const parentB = bounds.get(node.parentId);
        if (!childB || !parentB) continue;

        // Determine direction based on relative position
        const childIsRight = childB.centerX >= parentB.centerX;
        const fromX = childIsRight ? parentB.x + parentB.width : parentB.x;
        const toX = childIsRight ? childB.x : childB.x + childB.width;

        connectors.push({
          parentId: node.parentId,
          childId: id,
          path: cubicBezierPath(
            { x: fromX, y: parentB.centerY },
            { x: toX, y: childB.centerY },
          ),
        });
      }
    }

    // Compute total bounding rect
    const totalBounds = computeTotalBounds(bounds);

    return { nodeBounds: bounds, connectorPaths: connectors, totalBounds };
  },
};

function hasCollapsedAncestor(node: MindMapNode, nodes: Map<string, MindMapNode>): boolean {
  let current = node;
  while (current.parentId) {
    const parent = nodes.get(current.parentId);
    if (!parent) return false;
    if (parent.collapsed) return true;
    current = parent;
  }
  return false;
}

function getDepth(node: MindMapNode, nodes: Map<string, MindMapNode>): number {
  let depth = 0;
  let current = node;
  while (current.parentId) {
    depth++;
    const parent = nodes.get(current.parentId);
    if (!parent) break;
    current = parent;
  }
  return depth;
}

function computeTotalBounds(bounds: Map<string, NodeBounds>): BoundingRect {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const b of bounds.values()) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
